import { MotionReveal } from "../components/MotionReveal";
import {
  Activity,
  Bot,
  Brain,
  CircleCheck,
  Cloud,
  Gauge,
  GitBranch,
  Network,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";

const capabilities = [
  {
    title: "Unified Observability",
    desc: "IT + Cloud + Network + User Experience — a single operational view across signal types with topology correlation.",
    icon: Activity,
  },
  {
    title: "AI-Driven Root Cause Analysis",
    desc: "Topology-aware causality detection and automated RCA that cuts time-to-understand by up to 90%.",
    icon: Brain,
  },
  {
    title: "Predictive Anomaly Detection",
    desc: "ML baselines and forecasting to identify abnormal behavior before business impact.",
    icon: TrendingUp,
  },
  {
    title: "Digital Experience Monitoring",
    desc: "RUM + synthetic journeys to protect Customer experience and meet executive SLAs.",
    icon: Gauge,
  },
  {
    title: "Network & Packet Intelligence",
    desc: "Deep network visibility with flow/packet analytics for performance and forensics.",
    icon: Network,
  },
  {
    title: "Cloud & Kubernetes Observability",
    desc: "Container, pod, service mesh and cloud cost correlation for modern platforms.",
    icon: Cloud,
  },
  {
    title: "SRE & Reliability Engineering",
    desc: "SLI/SLO governance, error budgets, and reliability practices engineered into operations.",
    icon: GitBranch,
  },
  {
    title: "Automated Remediation & Runbooks",
    desc: "Event-driven automation and self-healing workflows integrated with ITSM and orchestration.",
    icon: Bot,
  },
  {
    title: "Capacity Planning & Forecasting",
    desc: "Trend-driven capacity insight for right-sizing, growth planning, and budget alignment.",
    icon: TrendingUp,
  },
  {
    title: "Compliance & SLA/SLO Monitoring",
    desc: "Audit-ready reporting and SLA/SLO adherence to reduce risk and contractual exposure.",
    icon: ShieldCheck,
  },
];

export default function CapabilitiesPage() {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="pt-6 sm:pt-10">
        <MotionReveal>
          <div className="grid gap-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">
              Enterprise delivery, measurable outcomes
            </div>
            <div>
              <h1 className="font-[var(--font-display)] text-4xl sm:text-5xl tracking-tight text-white">
                Enterprise Observability Capabilities
              </h1>
              <p className="mt-4 max-w-3xl text-sm sm:text-base text-[var(--text-secondary)]">
                From telemetry to decisions. From alerts to actions.
              </p>
            </div>
          </div>
        </MotionReveal>
      </div>

      <section className="mt-10 sm:mt-14">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {capabilities.map((c, idx) => (
            <MotionReveal key={c.title} delay={Math.min(0.35, idx * 0.04)}>
              <details className="glass-panel hover-lift group rounded-3xl p-6">
                <summary className="flex cursor-pointer list-none items-start gap-4">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-white/10 bg-white/5 text-[var(--accent-primary)]">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-[var(--font-display)] text-lg tracking-tight text-white">
                        {c.title}
                      </div>
                      <CircleCheck className="h-4 w-4 text-white/40 group-open:text-[var(--accent-secondary)]" />
                    </div>
                    <div className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
                      {c.desc}
                    </div>
                  </div>
                </summary>
                <div className="mt-5 rounded-2xl border border-white/10 bg-white/3 p-4">
                  <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">
                    Executive value lens
                  </div>
                  <div className="mt-2 text-sm text-white">
                    Faster resolution, reduced risk, and higher operational efficiency — engineered into an integrated operating model.
                  </div>
                </div>
              </details>
            </MotionReveal>
          ))}
        </div>
      </section>

      <section className="mt-16 sm:mt-20">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-8 sm:p-10">
            <div className="flex items-start gap-4">
              <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/5 text-[var(--accent-secondary)]">
                <Brain className="h-5 w-5" />
              </div>
              <div>
                <div className="font-[var(--font-display)] text-2xl tracking-tight text-white">
                  AIOps is not a dashboard — it’s a decision engine
                </div>
                <p className="mt-3 max-w-3xl text-sm sm:text-base text-[var(--text-secondary)]">
                  We convert raw signals into correlated insights, predictive intelligence, and automated remediation.
                  The outcome is fewer incidents, faster MTTR, and measurable ROI.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href="/architecture"
                    className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-white border border-white/15 bg-white/5 hover:bg-white/8"
                  >
                    View Reference Architecture
                  </a>
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-[#061019] bg-[var(--accent-primary)] hover:brightness-110"
                  >
                    Request Assessment
                  </a>
                </div>
              </div>
            </div>
          </div>
        </MotionReveal>
      </section>

      <div className="h-12" />
    </div>
  );
}
